import { useTheme } from '@mui/material/styles';
import { compareAsc, isAfter } from 'date-fns';
import { navigate } from 'gatsby';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import NullStateDiv from '@components/NullStateDiv';
import { UpcomingAppointmentCard } from '@components/UpcomingAppointmentCard';

// import { useResponses } from '@contexts/Survey';
// import { APPOINTMENTS_ID, SURGERY_ID } from '@constants/surveyIds';
import {
  ADD_APPOINTMENT,
  APPOINTMENT_HISTORY,
  SURGERIES,
} from '@constants/routes';

import { useGetAppointments } from '@hooks/api/appointment';
import { useGetProcedures } from '@hooks/api/procedure';

import AddRecord from '../AddRecord';
import { DashHeader } from '../Common.style';

const messages = defineMessages({
  title: {
    id: 'upcomingAppointments.title',
    defaultMessage: 'Upcoming Appointments',
  },
  noAppointments: {
    id: 'upcomingAppointments.noAppointments',
    defaultMessage:
      'No appointments currently scheduled. To see appointments here, add an appointment by clicking the button below or from the Appointment section.',
  },
  addAppointment: {
    id: 'upcomingAppointments.addAppointment',
    defaultMessage: 'Add Appointment',
  },
});

type UpcomingAppointment = {
  date: Date | null;
  title: string;
  subtitle?: string | null;
  colour: string;
  onClick: () => void;
};

function UpcomingAppointments() {
  const { formatMessage } = useIntl();

  const { palette } = useTheme();

  const { data: appointmentsData, loading: appointmentsLoading } =
    useGetAppointments();
  const { data: surgeriesData, loading: surgeriesLoading } = useGetProcedures();

  // Combined upcoming state
  let upcoming: UpcomingAppointment[] = [];

  appointmentsData.forEach((appointment) => {
    upcoming.push({
      date: appointment.getVisitDate(),
      title: appointment.visitReason ?? 'Appointment',
      subtitle: appointment.facilityName,
      colour: palette.secondary.main,
      onClick: () => {
        navigate(`${APPOINTMENT_HISTORY}${appointment.id}`);
      },
    });
  });

  surgeriesData.forEach((proc) => {
    upcoming.push({
      date: proc.getProcedureDate(),
      title: proc.visitReason ?? 'Surgery',
      subtitle: proc.facility,
      colour: palette.primary.light,
      onClick: () => {
        navigate(`${SURGERIES}${proc.id}`);
      },
    });
  });

  upcoming = upcoming
    .filter(({ date }) => date && isAfter(date, new Date()))
    .sort((a, b) =>
      // eslint-disable-next-line no-nested-ternary
      a.date && b.date ? compareAsc(a.date, b.date) : a.date ? 1 : -1
    );

  return (
    <div>
      <DashHeader>{formatMessage(messages.title)}</DashHeader>

      <ContentLoader loading={surgeriesLoading || appointmentsLoading}>
        {upcoming.length > 0 ? (
          upcoming.map((appointment, index) => (
            <UpcomingAppointmentCard
              key={`appointments-upcoming-${index}`}
              {...appointment}
              date={appointment.date}
              fullWidth
            />
          ))
        ) : (
          <NullStateDiv>
            {formatMessage(messages.noAppointments)}
            <AddRecord
              route={ADD_APPOINTMENT}
              text={formatMessage(messages.addAppointment)}
            />
          </NullStateDiv>
        )}
      </ContentLoader>
    </div>
  );
}

export default withErrorBoundary(UpcomingAppointments, { fallback: null });
